import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet } from '../components/Sheet.jsx';
import { useCreateRegie, useCreateRegieFuerKunde, useKundenSuche } from '../hooks/data.js';
import { useToast } from '../state/toast.jsx';
import { newKey } from '../lib/outbox.js';
import { Icon } from '../components/Icon.jsx';

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

/**
 * Regieauftrag/Regierapport: legt in Exxas einen neuen Auftrag an und öffnet ihn direkt bei «Zeit & Material».
 * Mit `nr` (aus einem Auftrag): derselbe Kunde. Ohne `nr` (Startseite): zuerst den Kunden suchen und wählen.
 * Braucht Netz – ohne Exxas gibt es keine Auftragsnummer.
 */
export function RegieSheet({ nr, auftrag, onClose }) {
  const ausAuftrag = useCreateRegie(nr);
  const fuerKunde = useCreateRegieFuerKunde();
  const create = nr ? ausAuftrag : fuerKunde;
  const navigate = useNavigate();
  const toast = useToast();
  const [kunde, setKunde] = useState(null); // nur ohne nr
  const [q, setQ] = useState('');
  const term = useDebounced(q.trim(), 400);
  const suche = useKundenSuche(nr || kunde ? '' : term);
  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [key] = useState(newKey); // gleicher Schlüssel bei «nochmals» → nie zwei Aufträge
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!nr && !kunde) { setError('Bitte zuerst den Kunden wählen.'); return; }
    if (!titel.trim()) { setError('Bitte einen Titel eingeben.'); return; }
    setError('');
    try {
      const daten = { titel: titel.trim(), beschreibung: beschreibung.trim() || undefined, key };
      const r = await create.mutateAsync(nr ? daten : { ...daten, kundeId: kunde.id });
      toast({ text: `Regieauftrag ${r.auftrag.nummer} erstellt – jetzt Zeit und Material erfassen`, long: true });
      onClose();
      navigate(`/auftrag/${encodeURIComponent(r.auftrag.nummer)}/zeit-material`);
    } catch (err) {
      setError(err.message);
    }
  };

  const ort = (k) => [k.strasse, [k.plz, k.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ');

  return (
    <Sheet title={nr ? 'Regieauftrag erstellen' : 'Regierapport erstellen'} onClose={onClose}>
      <form className="stack" style={{ gap: 14 }} onSubmit={submit}>
        {nr ? (
          <p className="sub" style={{ lineHeight: 1.45 }}>
            Neuer Auftrag für <strong>{auftrag?.kunde?.firma || 'denselben Kunden'}</strong>. Danach erfasst du Zeit und Material direkt darauf.
          </p>
        ) : kunde ? (
          <div className="card" style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, background: 'var(--bg)' }}>
            <Icon name="user" size={20} />
            <span className="stack" style={{ gap: 0, flex: 1, minWidth: 0 }}>
              <span className="ellipsis" style={{ fontWeight: 800 }}>{kunde.name}</span>
              <span className="sub ellipsis" style={{ fontSize: 12 }}>{[kunde.nummer, ort(kunde)].filter(Boolean).join(' · ')}</span>
            </span>
            <button type="button" className="linkbtn" onClick={() => { setKunde(null); setError(''); }} disabled={create.isPending}>Ändern</button>
          </div>
        ) : (
          <div className="stack" style={{ gap: 8 }}>
            <label className="field">
              <span className="label">Kunde *</span>
              <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, Nummer oder Ort" autoComplete="off" enterKeyHint="search" />
            </label>
            {term.length >= 2 && suche.isLoading && <div className="skeleton" style={{ height: 120 }} />}
            {suche.isError && <div className="notice err" role="alert"><Icon name="warn" size={18} />{suche.error.message}</div>}
            {term.length >= 2 && suche.data && !suche.data.length && <p className="sub">Kein Kunde gefunden.</p>}
            {(suche.data?.length ?? 0) > 0 && term.length >= 2 && (
              <div className="card list" style={{ padding: '2px 12px', background: 'var(--bg)' }}>
                {suche.data.map((k) => (
                  <button key={k.id} type="button" className="listrow" onClick={() => { setKunde(k); setError(''); }}
                    style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', borderTop: '1px solid var(--line-2)', font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
                    <span className="stack" style={{ gap: 0, flex: 1, minWidth: 0 }}>
                      <span className="ellipsis" style={{ fontWeight: 700 }}>{k.name}</span>
                      <span className="sub ellipsis" style={{ fontSize: 12 }}>{[k.nummer, ort(k)].filter(Boolean).join(' · ')}</span>
                    </span>
                    <Icon name="chevron" size={16} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {(nr || kunde) && (
          <>
            <label className="field">
              <span className="label">Titel *</span>
              <input className="input" value={titel} onChange={(e) => setTitel(e.target.value)} maxLength={120} placeholder="z. B. Zusätzliche Zone Vorgarten" enterKeyHint="done" required disabled={create.isPending} />
            </label>
            <label className="field">
              <span className="label">Beschreibung (optional)</span>
              <textarea className="input" rows={3} style={{ minHeight: 70 }} value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} maxLength={1000} placeholder="Was wurde zusätzlich gewünscht?" disabled={create.isPending} />
            </label>
          </>
        )}
        {error && <div className="notice err" role="alert"><Icon name="warn" size={18} />{error}</div>}
        {(nr || kunde) && (
          <button className="btn" type="submit" disabled={create.isPending || !titel.trim()}>
            {create.isPending ? 'Wird in Exxas angelegt …' : 'Erstellen & erfassen'}
          </button>
        )}
      </form>
    </Sheet>
  );
}
